/**
 * ReadingRuler — iter-92
 * =======================
 * A movable horizontal guide that overlays the Manual Entry canvas
 * so authors can eyeball where each printed page will fall in the
 * flow, roughly estimate chapter length, and read long passages
 * without losing their line.
 *
 * Modes:
 *   • Follow (default) — the ruler tracks the pointer Y as it moves
 *     inside the canvas.
 *   • Locked          — click anywhere in the canvas to pin the
 *     ruler in place; click again to unlock.
 *
 * The badge attached to the right of the line shows an approximate
 * page number using a fixed density (default 275 words / page for a
 * 6×9 trade paperback) computed against the total word count of the
 * book — accurate enough for eyeballing without paginating in the
 * browser.
 *
 * Props:
 *   containerRef   — React ref to the scroll container (editor col)
 *   enabled        — bool; false renders nothing
 *   totalWords     — number; used for the page estimate
 *   wordsPerPage   — number; default 275
 */
import React, { useEffect, useRef, useState } from "react";
import { Pin, PinOff } from "lucide-react";

export function ReadingRuler({ containerRef, enabled, totalWords, wordsPerPage = 275 }) {
  // Content-Y coordinate (scrollTop + mouse offset within container)
  // where the ruler currently sits. `null` means "not yet positioned".
  const [contentY, setContentY] = useState(null);
  const [locked, setLocked] = useState(false);
  // Live viewport-Y for rendering (contentY - scrollTop).
  const [viewY, setViewY] = useState(0);
  const rafRef = useRef();

  // Attach pointer + scroll listeners once and clean up.
  useEffect(() => {
    if (!enabled) return;
    const el = containerRef?.current;
    if (!el) return;

    const onMove = (ev) => {
      if (locked) return;
      const rect = el.getBoundingClientRect();
      const yInView = ev.clientY - rect.top;
      if (yInView < 0 || yInView > rect.height) return;
      // schedule render on rAF so 60fps mouse never overwhelms React
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setContentY(el.scrollTop + yInView);
        setViewY(yInView);
      });
    };

    const onClick = (ev) => {
      // Ignore clicks on form fields / buttons so the ruler doesn't
      // fight with editing. Only lock when clicking directly on the
      // scroll container's whitespace (target === container itself
      // or an obvious empty div).
      const tag = (ev.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select", "button", "a", "label"].includes(tag)) return;
      if (ev.target?.isContentEditable) return;
      setLocked((v) => !v);
    };

    const onScroll = () => {
      if (contentY == null) return;
      setViewY(contentY - el.scrollTop);
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("click", onClick);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("click", onClick);
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef, enabled, locked, contentY]);

  if (!enabled || contentY == null) return null;

  const el = containerRef.current;
  const rect = el?.getBoundingClientRect();
  if (!el || !rect) return null;

  // Only render when the ruler falls inside the visible viewport.
  if (viewY < 0 || viewY > rect.height) return null;

  // Approximate page number: pixel ratio × book pages.
  const scrollHeight = Math.max(el.scrollHeight, 1);
  const totalPages = Math.max(1, Math.round((totalWords || 0) / wordsPerPage));
  const approxPage = Math.max(
    1,
    Math.min(totalPages, Math.ceil((contentY / scrollHeight) * totalPages))
  );

  return (
    <div
      data-testid="mc-reading-ruler"
      className="pointer-events-none absolute left-0 right-0 z-30"
      style={{ top: viewY - 12 }}>
      {/* Two-tone tinted band that makes the current text line pop.
          Border-bottom is the true guide line; the softer top band
          gives a peripheral "reading strip" effect. */}
      <div className="h-6 bg-[color:var(--primary)]/8 border-t border-b border-[color:var(--primary)]/40"/>
      <div className="absolute right-4 -top-[9px] flex items-center gap-2
                       bg-[color:var(--primary)] text-white text-[10px]
                       uppercase tracking-widest px-2 py-1 shadow-sm">
        {locked
          ? <Pin className="w-3 h-3" strokeWidth={2}/>
          : <PinOff className="w-3 h-3" strokeWidth={1.5}/>}
        ≈ page {approxPage} / {totalPages}
      </div>
    </div>
  );
}

export default ReadingRuler;
